import { AirQualityComponent, AirQualityUnit } from "@/db/definitions";

export const airQuality = [
  "GOOD",
  "MODERATE",
  "INSUFFICIENT",
  "BAD",
  "VERY_BAD",
] as const;

export type AirQuality = (typeof airQuality)[number];

export const AIR_QUALITY_COLORS: Record<AirQuality, { bg: string }> = {
  GOOD: {
    bg: "#22C55E",
  },
  MODERATE: {
    bg: "#EAB308",
  },
  INSUFFICIENT: {
    bg: "#F97316",
  },
  BAD: {
    bg: "#EF4444",
  },
  VERY_BAD: {
    bg: "#A21CAF",
  },
};

export const AIR_COMPONENT_BANDS: Record<
  AirQualityComponent,
  { max: number | null; q: AirQuality }[]
> = {
  NO_2: [
    { max: 20, q: "GOOD" },
    { max: 45, q: "MODERATE" },
    { max: 75, q: "INSUFFICIENT" },
    { max: 125, q: "BAD" },
    { max: null, q: "VERY_BAD" },
  ],
  PM_10: [
    { max: 20, q: "GOOD" },
    { max: 45, q: "MODERATE" },
    { max: 75, q: "INSUFFICIENT" },
    { max: 125, q: "BAD" },
    { max: null, q: "VERY_BAD" },
  ],
  PM_2_5: [
    { max: 15, q: "GOOD" },
    { max: 30, q: "MODERATE" },
    { max: 50, q: "INSUFFICIENT" },
    { max: 90, q: "BAD" },
    { max: null, q: "VERY_BAD" },
  ],
  O_3: [
    { max: 30, q: "GOOD" },
    { max: 60, q: "MODERATE" },
    { max: 100, q: "INSUFFICIENT" },
    { max: 180, q: "BAD" },
    { max: null, q: "VERY_BAD" },
  ],
  LKI: [],
};

export function classifyAirQualityBand(
  component: AirQualityComponent,
  value: number
): AirQuality {
  for (const { max, q } of AIR_COMPONENT_BANDS[component]) {
    if (max === null || value <= max) return q;
  }
  return "VERY_BAD";
}

export const airComponentBaseData: Record<
  AirQualityComponent,
  {
    slug: string;
    formula: string;
    unit: AirQualityUnit;
  }
> = {
  LKI: {
    slug: "lki",
    formula: "LKI",
    unit: "INDEX",
  },
  NO_2: {
    slug: "no2",
    formula: "NO₂",
    unit: "UG_M3",
  },
  O_3: {
    slug: "o3",
    formula: "O₃",
    unit: "UG_M3",
  },
  PM_10: {
    slug: "pm10",
    formula: "PM₁₀",
    unit: "UG_M3",
  },
  PM_2_5: {
    slug: "pm25",
    formula: "PM₂.₅",
    unit: "UG_M3",
  },
};

export const airComponentSlug = {
  getComponent: (slug: string): AirQualityComponent | null => {
    const entry = Object.entries(airComponentBaseData).find(
      ([, data]) => data.slug === slug
    );
    return entry ? (entry[0] as AirQualityComponent) : null;
  },
  getSlug: (component: AirQualityComponent): string => {
    return airComponentBaseData[component].slug;
  },
};

export const airStationSlug = {
  getId: (slug: string): string => {
    return slug.toUpperCase();
  },
  getSlug: (id: string): string => {
    return id.toLowerCase();
  },
};
